import { LikertStatement, Question, Section } from '../types/form-engine.types';

/** Matched by statement text so statements added to (or removed from) the form are never resurrected or lost. */
const mergeLikertStatements = (current: LikertStatement[], saved?: LikertStatement[]): LikertStatement[] => {
  if (!Array.isArray(saved)) return current;
  const savedByStatement = new Map(saved.filter((s) => s?.Statement).map((s) => [s.Statement, s.Value]));
  return current.map((statement) =>
    savedByStatement.has(statement.Statement) ? { ...statement, Value: savedByStatement.get(statement.Statement) ?? [] } : statement
  );
};

/** Copies answers only; the question structure always comes from the current form. */
const applyDraftAnswer = (question: Question, saved?: Partial<Question>): Question => {
  if (!saved) return question;
  return {
    ...question,
    Answer: saved.Answer,
    Rating: saved.Rating,
    McOptionsValue: saved.McOptionsValue,
    CbOptionsValues: saved.CbOptionsValues,
    Comments: saved.Comments,
    LikertStatements: mergeLikertStatements(question.LikertStatements ?? [], saved.LikertStatements),
  };
};

export const mergeFlatDraft = (current: Question[], draftQuestions: Partial<Question>[]): Question[] =>
  current.map((question) => applyDraftAnswer(question, draftQuestions.find((item) => item.ItemId === question.ItemId)));

export const mergeSectionDraft = (current: Section[], draftSections: Section[]): Section[] => {
  const saved = new Map<string, Question>();
  draftSections.forEach((section) =>
    section.Questions?.forEach((question) => saved.set(`${section.SectionId}~${question.ItemId}`, question))
  );
  return current.map((section) => ({
    ...section,
    Questions: section.Questions.map((question) => applyDraftAnswer(question, saved.get(`${section.SectionId}~${question.ItemId}`))),
  }));
};

const questionSignature = (question: Question) =>
  [
    question.ItemId,
    question.FormatType,
    question.IsRequired ? 1 : 0,
    question.HasComment ? 1 : 0,
    question.MinRating ?? '',
    question.MaxRating ?? '',
    (question.Options ?? []).map((o) => `${o.Value}:${o.Action ?? ''}:${o.TargetSectionId ?? ''}`).join('|'),
    (question.LikertLabels ?? []).join('|'),
    (question.LikertStatements ?? []).map((s) => s.Statement).join('|'),
  ].join('~');

/** Structural fingerprint (never answers) that identifies the exact form a draft was saved against. */
export const buildFormFingerprint = (
  form: { HasSections: boolean; FormData: Question[]; Sections: Section[] },
  formId = '',
  lastUpdatedDate = ''
) => {
  const parts = [formId, lastUpdatedDate];
  if (form.HasSections && form.Sections.length) {
    form.Sections.forEach((section) => {
      const questions = section.Questions.map(questionSignature).join(',');
      parts.push([section.SectionId, section.Position, questions].join('~'));
    });
  } else {
    form.FormData.forEach((q) => parts.push(questionSignature(q)));
  }
  return parts.join('#');
};
